import { useState } from 'react';
import { OrgAgreementTab } from '@/pages/org-agreement-tab';
import { OrgQuestionsTab } from '@/pages/org-questions-tab';
import { OrgResourcesTab } from '@/pages/org-resources-tab';
import { CoinsIcon, LayoutGridIcon, MessagesSquareIcon, UsersIcon } from 'lucide-react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { BackLink, Button, ConfirmDialog, DataTable, DatePicker, Drawer, EmptyState, ErrorNotice, FactList, Field, LedgerTable, LoadingState, PageEyebrow, Pagination, Panel, Pill, SearchInput, SelectInput, SkeletonTable, Tabs, TextArea, TextInput, useDebouncedValue, type PillTone, formatCredits } from '@bcecloud/portal-ui';
import { getJson, getList, sendJson, errorMessage } from '@/lib/api';
import { CampaignsTable } from '@/pages/CampaignsPage';
import { OrgEventsPanel } from '@/pages/org-events-panel';
import { OrgDemoPanel } from '@/pages/org-demo-panel';
import { OrgPartnerPhotosPanel } from '@/pages/org-partner-photos';
import { OrgPartnerListingPanel } from '@/pages/org-partner-listing';
import { OrgPartnerReferralsPanel } from '@/pages/org-partner-referrals';
import { OrgPartnerPerformancePanel } from '@/pages/org-partner-performance';
import { ImageUploadField } from '@/components/ImageUploadField';
import { MessagingProfileEditor } from '@/components/MessagingProfileEditor';
import { SalesforceAccountsPanel } from '@/components/SalesforceAccountsPanel';
import { TierEditor, parseTiers, tiersAscend, type TierDraft } from '@/components/TierEditor';
import { tiersSummary, type PricingTemplate } from '@/pages/PricingTemplatesPage';
import {
  ORG_ROLE_LABELS,
  ORG_ROLE_DESCRIPTIONS,
  PARTNER_ROLE_DESCRIPTIONS,
  type Brand,
  type Member,
  type OrganizationDetail,
  type OrgRole,
} from '@/lib/types';
import { ORGANIZATION_KINDS, PARTNER_TYPE_LABELS, type PartnerSummary } from '@/lib/types';

/**
 * One client's configuration: branding, members and portal address.
 *
 * @returns The organization detail page.
 */
/** Which section of a client's page is shown. */
type OrgTab =
  | 'questions'
  | 'credits'
  | 'bookings'
  | 'catalog'
  | 'resources'
  | 'enquiries'
  | 'campaigns'
  | 'branding'
  | 'messaging'
  | 'members'
  | 'agreement'
  | 'settings'
  | 'showcase'
  | 'referrals'
  | 'performance'
  | 'listing';

/** How the client is set up first; what is moving through their event lifecycle after the rule. */
const ORG_TABS = [
  { key: 'credits', label: 'Credits' },
  { key: 'catalog', label: 'Catalog' },
  { key: 'resources', label: 'Resources' },
  { key: 'branding', label: 'Branding' },
  { key: 'messaging', label: 'Messaging' },
  { key: 'members', label: 'Members' },
  { key: 'agreement', label: 'Agreement' },
  { key: 'settings', label: 'Settings' },
  { key: 'enquiries', label: 'Inquiries', dividerBefore: true },
  { key: 'campaigns', label: 'Campaigns' },
  { key: 'bookings', label: 'Events' },
  { key: 'questions', label: 'Questions' },
] as const satisfies readonly { key: OrgTab; label: string; dividerBefore?: boolean }[];

/**
 * A showcase partner has none of a client's credits, agreements or events:
 * its showcase, its branding and the people who sign in. Its programs and
 * words are the partner's to manage, from their own portal.
 */
const PARTNER_TABS = [
  { key: 'showcase', label: 'Showcase' },
  { key: 'performance', label: 'Performance' },
  { key: 'referrals', label: 'Referrals' },
  { key: 'listing', label: 'City page' },
  { key: 'branding', label: 'Branding' },
  { key: 'members', label: 'Members' },
] as const satisfies readonly { key: OrgTab; label: string }[];

export function OrganizationDetailPage(): React.ReactElement {
  const { organizationId = '' } = useParams();
  const [params, setParams] = useSearchParams();

  const organization = useQuery({
    queryKey: ['organization', organizationId],
    queryFn: ({ signal }) => getJson<OrganizationDetail>(`/organizations/${organizationId}`, undefined, signal),
  });

  if (organization.isPending) {
    return <LoadingState label="Loading…" />;
  }

  if (organization.error || !organization.data) {
    return <ErrorNotice title="Client unavailable" message={errorMessage(organization.error)} />;
  }

  const client = organization.data;
  const isPartner = client.kind === ORGANIZATION_KINDS.partner;
  const tabs: readonly { key: OrgTab; label: string; dividerBefore?: boolean }[] = isPartner ? PARTNER_TABS : ORG_TABS;
  const firstTab = tabs[0]?.key ?? 'credits';
  const activeTab: OrgTab = tabs.some((t) => t.key === params.get('tab')) ? (params.get('tab') as OrgTab) : firstTab;

  return (
    <section className="animate-portal-rise">
      <BackLink to="/organizations">All organizations</BackLink>

      <header className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div data-guide="organization-header">
          <PageEyebrow>{isPartner ? 'SHOWCASE PARTNER' : 'CLIENT PORTAL'}</PageEyebrow>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-portal-page tracking-[-1.7px]">{client.name}</h1>
            {client.isDemo ? <Pill tone="info">Demo</Pill> : null}
          </div>
          <p className="mt-1 text-sm text-admin-muted">
            <a href={client.portalUrl} target="_blank" rel="noreferrer" className="text-primary underline-offset-2 hover:underline">
              {client.portalUrl.replace(/^https?:\/\//, '')}
            </a>
            {' · '}
            {client._count.memberships} member{client._count.memberships === 1 ? '' : 's'}
            {isPartner ? null : (
              <>
                {' · '}
                {client._count.bookingRequests} booking request
                {client._count.bookingRequests === 1 ? '' : 's'}
              </>
            )}
          </p>
        </div>

        <Button guide="organization-open-portal" variant="secondary" href={client.portalUrl} target="_blank" rel="noreferrer">
          {isPartner ? 'Open their portal ↗' : 'Open portal ↗'}
        </Button>
      </header>

      <div className="mt-8">
        <Tabs
          guide="organization-tabs"
          label={isPartner ? 'Partner sections' : 'Client sections'}
          items={tabs.map((t) => ({ key: t.key, label: t.label, ...('dividerBefore' in t ? { dividerBefore: t.dividerBefore } : {}) }))}
          active={activeTab}
          onChange={(key) =>
            setParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set('tab', key);
              return next;
            })
          }
        />

        {activeTab === 'showcase' ? (
          <>
            <PartnerShowcasePanel partner={client.partner ?? null} portalUrl={client.portalUrl} />
            <OrgPartnerPhotosPanel organizationId={organizationId} organizationName={client.name} />
          </>
        ) : null}
        {activeTab === 'performance' ? <OrgPartnerPerformancePanel organizationId={organizationId} organizationName={client.name} /> : null}
        {activeTab === 'listing' ? <OrgPartnerListingPanel organizationId={organizationId} organizationName={client.name} /> : null}
        {activeTab === 'referrals' ? <OrgPartnerReferralsPanel organizationId={organizationId} organizationName={client.name} /> : null}

        {activeTab === 'questions' ? <OrgQuestionsTab organizationId={organizationId} /> : null}
        {activeTab === 'resources' ? <OrgResourcesTab organizationId={organizationId} organizationName={client.name} /> : null}
        {activeTab === 'bookings' ? (
          <OrgEventsPanel organizationId={organizationId} organizationName={client.name} />
        ) : null}

        {activeTab === 'credits' ? (
          <CreditsPanel organizationId={organizationId} organizationName={client.name} />
        ) : null}
        {activeTab === 'catalog' ? (
          <CataloguePanel organizationId={organizationId} organizationName={client.name} />
        ) : null}
        {activeTab === 'enquiries' ? (
          <>
            <BookingReviewPanel organizationId={organizationId} />
            <EnquiriesPanel organizationId={organizationId} />
          </>
        ) : null}
        {activeTab === 'campaigns' ? (
          <div className="mt-6">
            <TabHeading title="Campaigns" subtitle="One initiative delivered as several events. The client sends it; it is answered with a proposal, then the agreement." />
            <CampaignsTable organizationId={organizationId} />
          </div>
        ) : null}
        {activeTab === 'branding' ? (
          <BrandEditor organizationId={organizationId} brand={client.brand} />
        ) : null}
        {activeTab === 'messaging' ? (
          <div className="mt-6">
            <TabHeading
              title="Brand &amp; messaging profile"
              subtitle="Everything BCE builds this client's programs around. The client's own admins edit the same profile from their portal, and their members read it there."
            />
            <MessagingProfileEditor organizationId={organizationId} brand={client.brand} />
          </div>
        ) : null}
        {activeTab === 'members' ? <MemberRoster organizationId={organizationId} isPartner={isPartner} portalUrl={client.portalUrl} /> : null}
        {activeTab === 'agreement' ? (
          <OrgAgreementTab organizationId={organizationId} organizationName={client.name} />
        ) : null}
        {activeTab === 'settings' ? (
          <>
            <div data-guide="org-salesforce" className="mt-6">
              <TabHeading
                title="Salesforce accounts"
                subtitle={`The Salesforce accounts whose delivered events belong to ${client.name}. Link each account, or link a parent and include its child accounts so every region rolls in.`}
              />
              <SalesforceAccountsPanel
                organizationId={organizationId}
                organizationName={client.name}
              />
            </div>
            <PortalSettingsPanel
              organizationId={organizationId}
              organizationName={client.name}
            />
            <OrgDemoPanel organizationId={organizationId} organizationName={client.name} isDemo={client.isDemo === true} />
          </>
        ) : null}
      </div>
    </section>
  );
}

/**
 * A tab section's heading — outside the cards, the way the portal titles its
 * own sections ("Transaction history"). One size everywhere: these tabs
 * previously mixed three heading scales, and the smallest won by accident.
 *
 * @param props - Title and the muted line under it.
 * @returns The heading block.
 */
function TabHeading({
  title,
  subtitle,
}: {
  readonly title: string;
  readonly subtitle?: string;
}): React.ReactElement {
  return (
    <div className="mb-4">
      <h2 className="text-portal-section font-bold tracking-[-0.4px]">{title}</h2>
      {subtitle ? <p className="mt-1 text-portal-small text-portal-muted">{subtitle}</p> : null}
    </div>
  );
}

/** The BCE-managed portal settings for one client, with the global defaults. */
export interface OrgPortalSettings {
  readonly conciergeEmail: string | null;
  readonly conciergePhone: string | null;
  readonly defaults: {
    readonly email: string | null;
    readonly phone: string | null;
  };
}

/**
 * Per-client portal settings — currently the concierge contact overrides.
 *
 * Each field falls back to the system-wide default (shown beside it) when
 * left blank, so most clients need nothing here at all. The defaults
 * themselves are edited on the Portal defaults screen.
 *
 * @param props - The organization and its display name.
 * @returns The settings tab.
 */
function PortalSettingsPanel({
  organizationId,
  organizationName,
}: {
  readonly organizationId: string;
  readonly organizationName: string;
}): React.ReactElement {
  const queryClient = useQueryClient();

  const settings = useQuery({
    queryKey: ['organization-settings', organizationId],
    queryFn: ({ signal }) =>
      getJson<OrgPortalSettings>(`/organizations/${organizationId}/settings`, undefined, signal),
  });

  if (settings.isPending) return <LoadingState label="Loading settings…" />;
  if (settings.isError) {
    return <ErrorNotice title="Settings unavailable" message={errorMessage(settings.error)} />;
  }

  return (
    <PortalSettingsForm
      key={`${settings.data.conciergeEmail ?? ''}|${settings.data.conciergePhone ?? ''}`}
      organizationName={organizationName}
      settings={settings.data}
      onSave={async (patch) => {
        await sendJson('patch', `/organizations/${organizationId}/settings`, patch);
        await queryClient.invalidateQueries({
          queryKey: ['organization-settings', organizationId],
        });
      }}
    />
  );
}

/**
 * The settings form, mounted per loaded value so fields initialize from data.
 *
 * @param props - The loaded settings and the save action.
 * @returns The form.
 */
function PortalSettingsForm({
  organizationName,
  settings,
  onSave,
}: {
  readonly organizationName: string;
  readonly settings: OrgPortalSettings;
  readonly onSave: (patch: {
    conciergeEmail: string | null;
    conciergePhone: string | null;
  }) => Promise<void>;
}): React.ReactElement {
  const [email, setEmail] = useState(settings.conciergeEmail ?? '');
  const [phone, setPhone] = useState(settings.conciergePhone ?? '');

  const save = useMutation({
    mutationFn: () =>
      onSave({
        conciergeEmail: email.trim() === '' ? null : email.trim(),
        conciergePhone: phone.trim() === '' ? null : phone.trim(),
      }),
  });

  const dirty =
    email.trim() !== (settings.conciergeEmail ?? '') ||
    phone.trim() !== (settings.conciergePhone ?? '');

  return (
    <div className="mt-6">
      <TabHeading
        title="Concierge routing"
        subtitle={`Where ${organizationName}’s concierge questions are emailed, and the phone number their help page shows. Leave a field blank to use the portal-wide default.`}
      />

      <Panel className="max-w-[640px] p-6">
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (dirty && !save.isPending) save.mutate();
        }}
      >
        <Field
          label="Concierge email"
          htmlFor="portal-concierge-email"
          hint={
            settings.defaults.email
              ? `Default: ${settings.defaults.email}`
              : 'No portal-wide default is set — configure one under Portal defaults.'
          }
        >
          <TextInput
            id="portal-concierge-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder={settings.defaults.email ?? 'concierge@bestcorporateevents.com'}
          />
        </Field>

        <Field
          label="Concierge phone"
          htmlFor="portal-concierge-phone"
          hint={
            settings.defaults.phone
              ? `Default: ${settings.defaults.phone}`
              : 'No portal-wide default is set — configure one under Portal defaults.'
          }
        >
          <TextInput
            id="portal-concierge-phone"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            placeholder={settings.defaults.phone ?? '(800) 849-8326'}
          />
        </Field>

        {save.isError ? (
          <ErrorNotice inline title="Could not save" message={errorMessage(save.error)} />
        ) : null}
        {save.isSuccess && !dirty ? (
          <p role="status" className="text-sm text-emerald-700">
            Saved.
          </p>
        ) : null}

        <Button type="submit" disabled={!dirty || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save settings'}
        </Button>
      </form>
      </Panel>
    </div>
  );
}

/** The shared credit pool balance. */
export interface OrgCredits {
  readonly balance: {
    readonly available: number;
    readonly purchased: number;
    readonly reserved: number;
    readonly committed: number;
    readonly consumed: number;
  };
}

/** One row of the credit ledger. */
export interface LedgerRow {
  readonly id: string;
  readonly type: string;
  readonly amount: number;
  readonly reason?: string;
  readonly reference?: string;
  readonly actorName?: string;
  readonly createdAt: string;
}

/** Page size for the ledger — matches the server default. */
const LEDGER_PAGE_SIZE = 10;

interface CreditsPanelProps {
  readonly organizationId: string;
  readonly organizationName: string;
}

/**
 * The client's shared credit pool, and the control to apply more.
 *
 * Applying credits appends a PURCHASE to the append-only ledger — the way
 * credits enter the organization when an agreement is signed or topped up.
 * The balance shown is always derived from that ledger, never a stored number.
 *
 * @param props - The organization to manage credits for.
 * @returns The credits panel.
 */
function CreditsPanel({ organizationId, organizationName }: CreditsPanelProps): React.ReactElement {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [page, setPage] = useState(1);

  const credits = useQuery({
    queryKey: ['org-credits', organizationId],
    queryFn: ({ signal }) =>
      getJson<OrgCredits>(`/organizations/${organizationId}/credits`, undefined, signal),
  });

  const ledger = useQuery({
    queryKey: ['org-ledger', organizationId, page],
    queryFn: ({ signal }) =>
      getList<LedgerRow>(
        `/organizations/${organizationId}/credits/ledger`,
        { page, limit: LEDGER_PAGE_SIZE },
        signal,
      ),
    placeholderData: keepPreviousData,
  });

  const apply = useMutation({
    mutationFn: () =>
      sendJson(`post`, `/organizations/${organizationId}/credits`, {
        amount: Number(amount),
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        // Blank means these credits never expire. Sent as end-of-day UTC so a
        // batch dated the 31st is still spendable on the 31st.
        ...(expiresAt ? { expiresAt: new Date(`${expiresAt}T23:59:59Z`).toISOString() } : {}),
      }),
    onSuccess: async () => {
      setAmount('');
      setReference('');
      setReason('');
      setExpiresAt('');
      setPage(1);
      await queryClient.invalidateQueries({ queryKey: ['org-credits', organizationId] });
      await queryClient.invalidateQueries({ queryKey: ['org-ledger', organizationId] });
    },
  });

  const parsedAmount = Number(amount);
  const canApply =
    Number.isInteger(parsedAmount) && parsedAmount > 0 && !apply.isPending;

  return (
    <div className="mt-6">
      <TabHeading
        title="Credits"
        subtitle={`${organizationName}’s shared pool. Apply credits when an agreement is signed or topped up.`}
      />

      <CreditChangeQueue organizationId={organizationId} />

      {credits.isPending ? (
        <LoadingState label="Loading credits…" />
      ) : credits.error || !credits.data ? (
        <ErrorNotice inline message={errorMessage(credits.error)} />
      ) : (
        <Panel guide="org-credits" className="p-6">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <CreditStat label="Available" value={credits.data.balance.available} emphasis />
            <CreditStat label="Purchased" value={credits.data.balance.purchased} />
            <CreditStat label="Reserved" value={credits.data.balance.reserved} />
            <CreditStat label="Committed" value={credits.data.balance.committed} />
          </div>

          <form
            className="mt-6 flex flex-wrap items-end gap-3 border-t border-portal-line pt-6"
            onSubmit={(event) => {
              event.preventDefault();
              if (canApply) apply.mutate();
            }}
          >
            <Field label="Credits" htmlFor="credit-amount" className="w-[110px]">
              <TextInput
                id="credit-amount"
                type="number"
                min={1}
                step={1}
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                placeholder="60"
              />
            </Field>
            <Field label="PO / invoice #" htmlFor="credit-reference" className="w-[180px]">
              <TextInput
                id="credit-reference"
                value={reference}
                onChange={(event) => setReference(event.target.value)}
                placeholder="PO-4021"
              />
            </Field>
            <Field label="Expires" htmlFor="credit-expires" className="w-[190px]">
              {/* The placeholder carries the meaning of an empty value, so the
                  row keeps one line of labels — a hint under a single field
                  pushes that field out of line with its neighbours. */}
              <DatePicker
                id="credit-expires"
                value={expiresAt}
                onChange={setExpiresAt}
                placeholder="Never expires"
              />
            </Field>
            <Field label="Note" htmlFor="credit-reason" className="min-w-[200px] flex-1">
              <TextInput
                id="credit-reason"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="2026 agreement — 60 credits"
              />
            </Field>
            <Button type="submit" variant="primary" disabled={!canApply}>
              {apply.isPending ? 'Applying…' : 'Apply credits'}
            </Button>
          </form>

          {apply.error ? (
            <ErrorNotice inline message={errorMessage(apply.error)} />
          ) : null}
        </Panel>
      )}

      {credits.isPending || credits.error || !credits.data ? null : (
        <div className="mt-8">
          <TabHeading title="Activity" subtitle="Every movement, with who made it and why." />
          <div>
            {ledger.isError ? (
              <ErrorNotice
                title="Couldn’t load activity"
                message={errorMessage(ledger.error)}
                action={
                  <Button variant="secondary" onClick={() => void ledger.refetch()}>
                    Try again
                  </Button>
                }
              />
            ) : ledger.isPending ? (
              <SkeletonTable rows={5} columns={['w-[40%]', 'w-[10%]']} label="Loading activity…" />
            ) : ledger.data.items.length === 0 ? (
              <Panel>
                <EmptyState
                  icon={<CoinsIcon aria-hidden className="size-6" />}
                  title="No credit activity yet"
                  message="Every purchase, allocation and booking against this client’s credits will appear here. Apply credits above to record the first."
                />
              </Panel>
            ) : (
              <>
                {/* The same ledger table the client portal renders — one
                    component, so the two histories can never drift apart. */}
                <div className={ledger.isFetching ? 'opacity-60 transition-opacity' : undefined}>
                  <LedgerTable
                    rows={ledger.data.items.map((row) => ({
                      id: row.id,
                      date: row.createdAt,
                      label: CREDIT_TYPE_LABELS[row.type] ?? row.type,
                      details: [
                        row.reason ?? null,
                        [row.reference, row.actorName].filter(Boolean).join(' · ') || null,
                      ].filter((detail): detail is string => detail !== null),
                      amount: row.amount,
                    }))}
                  />
                </div>
                <Pagination
                  page={ledger.data.pagination.page}
                  totalPages={ledger.data.pagination.totalPages}
                  onChange={setPage}
                  summary={`${ledger.data.pagination.total} ${ledger.data.pagination.total === 1 ? 'entry' : 'entries'}`}
                />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Human labels for the ledger transaction types shown in the admin. */
const CREDIT_TYPE_LABELS: Readonly<Record<string, string>> = {
  PURCHASE: 'Applied',
  ALLOCATION: 'Allocated',
  RESERVATION: 'Reserved',
  COMMITMENT: 'Committed',
  CONSUMPTION: 'Consumed',
  RETURN: 'Returned',
  EXPIRATION: 'Expired',
  FORWARD: 'Carried forward',
  TRANSFER: 'Transferred',
  ADJUSTMENT: 'Adjusted',
};

/** One figure in the credit-balance row. */
function CreditStat({
  label,
  value,
  emphasis = false,
}: {
  readonly label: string;
  readonly value: number;
  readonly emphasis?: boolean;
}): React.ReactElement {
  return (
    <div className="rounded-portal-button bg-portal-bg p-3">
      <p className="text-portal-micro font-extrabold tracking-[0.6px] text-portal-muted uppercase">
        {label}
      </p>
      <p
        className={`mt-1 tabular-nums ${emphasis ? 'text-portal-stat font-bold text-portal-ink' : 'text-portal-card-title font-bold'}`}
      >
        {formatCredits(value)}
      </p>
    </div>
  );
}

interface BrandEditorProps {
  readonly organizationId: string;
  readonly brand: Brand | null;
}

/**
 * Edits a client's branding, with a live preview of the login screen's colors.
 *
 * The preview matters: these values drive a screen the client's employees see,
 * and picking two blues that fight is much easier to spot than to describe.
 *
 * @param props - Organization id and its current brand.
 * @returns The brand editor section.
 */
function BrandEditor({ organizationId, brand }: BrandEditorProps): React.ReactElement {
  const queryClient = useQueryClient();

  const [primaryColor, setPrimaryColor] = useState(brand?.primaryColor ?? '#00338d');
  const [sidebarColor, setSidebarColor] = useState(brand?.sidebarColor ?? '#001d52');
  const [logoUrl, setLogoUrl] = useState(brand?.logoUrl ?? '');
  const [logoDarkUrl, setLogoDarkUrl] = useState(brand?.logoDarkUrl ?? '');
  const [heroImageUrl, setHeroImageUrl] = useState(brand?.heroImageUrl ?? '');
  const [heroQuote, setHeroQuote] = useState(brand?.heroQuote ?? '');
  const [loginHeadline, setLoginHeadline] = useState(brand?.loginHeadline ?? '');
  const [welcomeMessage, setWelcomeMessage] = useState(brand?.welcomeMessage ?? '');

  const save = useMutation({
    mutationFn: () =>
      sendJson<Brand>('put', `/organizations/${organizationId}/brand`, {
        primaryColor,
        sidebarColor,
        // Empty means "clear this", which the API distinguishes from "untouched".
        logoUrl: logoUrl.trim() || null,
        logoDarkUrl: logoDarkUrl.trim() || null,
        heroImageUrl: heroImageUrl.trim() || null,
        heroQuote: heroQuote.trim() || null,
        loginHeadline: loginHeadline.trim() || null,
        welcomeMessage: welcomeMessage.trim() || null,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['organization', organizationId] }),
  });

  return (
    <div className="mt-6">
      <TabHeading
        title="Branding"
        subtitle="Applied to the client's portal at runtime — no rebuild or redeploy."
      />

      <Panel guide="org-branding" className="p-6">
      <form
        className="grid gap-6 md:grid-cols-[1fr_300px]"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <div className="space-y-5">
          <div className="flex gap-6">
            <ColorField id="brand-primary" label="Primary" value={primaryColor} onChange={setPrimaryColor} />
            <ColorField id="brand-sidebar" label="Sidebar" value={sidebarColor} onChange={setSidebarColor} />
          </div>

          <ImageUploadField
            label="Logo — light backgrounds"
            value={logoUrl}
            onChange={setLogoUrl}
            organizationId={organizationId}
            kind="logo"
            hint="Dark artwork for light surfaces: the login screen and white areas."
          />
          <ImageUploadField
            label="Logo — dark backgrounds"
            value={logoDarkUrl}
            onChange={setLogoDarkUrl}
            organizationId={organizationId}
            kind="logo-dark"
            hint="Light/white artwork for the sidebar and other dark surfaces. The portal picks the right variant from the sidebar color automatically."
          />
          <ImageUploadField
            label="Login hero image"
            value={heroImageUrl}
            onChange={setHeroImageUrl}
            organizationId={organizationId}
            kind="hero"
            hint="The full-height photo beside the sign-in form."
          />
          <TextField
            id="brand-headline"
            label="Login headline"
            value={loginHeadline}
            onChange={setLoginHeadline}
            placeholder="Bring your team together, anywhere."
          />
          <TextAreaField id="brand-quote" label="Hero quote" value={heroQuote} onChange={setHeroQuote} />
          <TextAreaField
            id="brand-welcome"
            label="Dashboard welcome message"
            value={welcomeMessage}
            onChange={setWelcomeMessage}
          />

          {save.error ? (
            <ErrorNotice inline title="Could not save branding" message={errorMessage(save.error)} />
          ) : null}

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={save.isPending}
              className="h-11 rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-sm hover:brightness-110 disabled:opacity-50"
            >
              {save.isPending ? 'Saving…' : 'Save branding'}
            </button>
            <span aria-live="polite" className="text-sm text-emerald-700">
              {save.isSuccess ? 'Saved.' : ''}
            </span>
          </div>
        </div>

        <BrandPreview
          primaryColor={primaryColor}
          sidebarColor={sidebarColor}
          logoUrl={logoUrl}
          headline={loginHeadline}
        />
      </form>
      </Panel>
    </div>
  );
}

interface BrandPreviewProps {
  readonly primaryColor: string;
  readonly sidebarColor: string;
  readonly logoUrl: string;
  readonly headline: string;
}

/**
 * A miniature of the client's login screen, updating as colors change.
 *
 * @param props - The brand values being edited.
 * @returns The preview panel.
 */
function BrandPreview({ primaryColor, sidebarColor, logoUrl, headline }: BrandPreviewProps): React.ReactElement {
  return (
    <aside>
      <p className="text-portal-tiny font-bold">Preview</p>
      <div className="mt-2 overflow-hidden rounded-lg border border-admin-line">
        <div className="grid grid-cols-[44%_56%]" style={{ height: 190 }}>
          <div className="bg-white p-3">
            {logoUrl ? (
              <img src={logoUrl} alt="" className="h-4 w-auto object-contain" />
            ) : (
              <div className="h-2 w-10 rounded-sm" style={{ background: primaryColor }} />
            )}
            <p className="mt-4 text-[9px] leading-tight font-bold" style={{ color: '#14213d' }}>
              {headline || 'Bring your team together, anywhere.'}
            </p>
            <div className="mt-3 h-4 w-full rounded-sm" style={{ background: primaryColor }} />
            <div className="mt-1.5 h-2 w-3/4 rounded-sm bg-slate-100" />
          </div>
          <div style={{ background: `linear-gradient(145deg, ${primaryColor}, ${sidebarColor})` }} />
        </div>
      </div>
      <p className="mt-2 text-xs text-admin-muted">
        Indicative only — open the portal to see the real screen.
      </p>
    </aside>
  );
}

interface MemberRosterProps {
  readonly organizationId: string;
  /** A showcase partner: partner wording, and adding someone quietly comes first. */
  readonly isPartner?: boolean;
  /** Where they sign in, for the note after adding someone without an email. */
  readonly portalUrl?: string;
}

/**
 * The client's approved contacts, and the form to invite another.
 *
 * @param props - Organization id.
 * @returns The member roster section.
 */
function MemberRoster({ organizationId, isPartner = false, portalUrl }: MemberRosterProps): React.ReactElement {
  const queryClient = useQueryClient();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [addedQuietly, setAddedQuietly] = useState<string | null>(null);
  const descriptions = isPartner ? PARTNER_ROLE_DESCRIPTIONS : ORG_ROLE_DESCRIPTIONS;
  const [role, setRole] = useState<OrgRole>('MEMBER');
  const [search, setSearch] = useState('');

  const members = useQuery({
    queryKey: ['members', organizationId],
    queryFn: ({ signal }) => getJson<Member[]>(`/organizations/${organizationId}/members`, undefined, signal),
  });
  const memberRows = matching(members.data ?? [], search, (member) => [
    member.firstName,
    member.lastName,
    member.email,
    ORG_ROLE_LABELS[member.role],
    ...member.teams.map((team) => team.name),
  ]);

  // Every portal account needs a name (the portal's sign-in requires one), so both paths ask for it.
  const ready = Boolean(email.trim() && firstName.trim() && lastName.trim());

  const [removing, setRemoving] = useState<Member | null>(null);
  const [resentTo, setResentTo] = useState<string | null>(null);
  const resend = useMutation({
    mutationFn: (member: Member) => sendJson<Member>('post', `/organizations/${organizationId}/members/${member.id}/resend`, {}),
    onSuccess: async (_data, member) => {
      setResentTo(member.email);
      await queryClient.invalidateQueries({ queryKey: ['members', organizationId] });
    },
  });
  const change = useMutation({
    mutationFn: ({ id, ...body }: { id: string; role?: OrgRole; isActive?: boolean }) =>
      sendJson<Member>('patch', `/organizations/${organizationId}/members/${id}`, body),
    onSuccess: async () => {
      setRemoving(null);
      await queryClient.invalidateQueries({ queryKey: ['members', organizationId] });
    },
  });

  const invite = useMutation({
    mutationFn: (sendInvitation: boolean) =>
      sendJson<Member>('post', `/organizations/${organizationId}/members`, {
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        role,
        sendInvitation,
        // Team standing is granted from inside the client portal, where the
        // people who know the team structure are. BCE staff set organization
        // authority and leave the rest to them.
        teams: [],
      }),
    onSuccess: async (_member, sendInvitation) => {
      setAddedQuietly(sendInvitation ? null : email.trim());
      setEmail('');
      setFirstName('');
      setLastName('');
      await queryClient.invalidateQueries({ queryKey: ['members', organizationId] });
      await queryClient.invalidateQueries({ queryKey: ['organization', organizationId] });
    },
  });

  return (
    <div className="mt-6">
      <TabHeading
        title={isPartner ? 'Partner team' : 'Approved contacts'}
        subtitle={
          isPartner
            ? 'Who at the partner can sign in to their portal. Add someone without an email to introduce the portal yourself, or send the standard invitation.'
            : 'Who may browse, request, approve and administer. Clerk sends the invitation; their authority is set here.'
        }
      />

      <Panel guide="org-members-invite" className="p-5">
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (ready) invite.mutate(!isPartner);
        }}
      >
        <Field label="First name" htmlFor="invite-first-name" required className="min-w-40">
          <TextInput id="invite-first-name" required autoComplete="off" value={firstName} onChange={(event) => setFirstName(event.target.value)} />
        </Field>
        <Field label="Last name" htmlFor="invite-last-name" required className="min-w-40">
          <TextInput id="invite-last-name" required autoComplete="off" value={lastName} onChange={(event) => setLastName(event.target.value)} />
        </Field>
        <div className="min-w-64 flex-1">
          <label htmlFor="invite-email" className="block text-portal-tiny font-bold">
            Work email
            <span className="ml-0.5 text-rose-600" title="Required">
              <span aria-hidden="true">*</span>
              <span className="sr-only"> (required)</span>
            </span>
          </label>
          <TextInput
            id="invite-email"
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder={isPartner ? 'name@theirvenue.com' : 'name@theircompany.com'}
          />
        </div>

        <div>
          <label htmlFor="invite-role" className="block text-portal-tiny font-bold">
            Role
          </label>
          <SelectInput
            id="invite-role"
            value={role}
            onChange={(event) => setRole(event.target.value as OrgRole)}
            aria-describedby="role-help"
          >
            {(Object.keys(ORG_ROLE_LABELS) as OrgRole[]).map((value) => (
              <option key={value} value={value}>
                {ORG_ROLE_LABELS[value]}
              </option>
            ))}
          </SelectInput>
        </div>

        <button
          type="submit"
          disabled={invite.isPending}
          className="h-11 rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-sm hover:brightness-110 disabled:opacity-50"
        >
          {invite.isPending ? 'Adding…' : isPartner ? 'Add without email' : 'Send invitation'}
        </button>
        <Button variant="secondary" type="button" disabled={invite.isPending || !ready} onClick={() => invite.mutate(isPartner)}>
          {isPartner ? 'Send invitation' : 'Add without email'}
        </Button>
      </form>

      <p id="role-help" className="mt-2 text-sm text-admin-muted">
        {descriptions[role]}
      </p>

      {addedQuietly ? (
        <p role="status" className="mt-3 rounded-md bg-emerald-50 p-3 text-sm text-emerald-800">
          Added {addedQuietly}. No email was sent. In your introduction, tell them to sign in at{' '}
          <span className="font-semibold">{portalUrl ? portalUrl.replace(/^https?:\/\//, '') : 'their portal'}</span> with their work email; a one-time code
          arrives in their inbox.
        </p>
      ) : null}

      {invite.error ? (
        <ErrorNotice inline title="Could not add them" message={errorMessage(invite.error)} />
      ) : null}
      </Panel>

      <div className="mt-6">
        {members.isPending ? <LoadingState label="Loading members…" /> : null}

        {members.data && members.data.length === 0 ? (
          <Panel>
            <EmptyState
              icon={<UsersIcon aria-hidden className="size-6" />}
              title={isPartner ? 'No one on the team yet' : 'No contacts yet'}
              message={
                isPartner
                  ? 'Add the person who will run the showcase as an organization admin, then send them your introduction.'
                  : "Invite the client's primary administrator to get started."
              }
            />
          </Panel>
        ) : null}

        {members.data && members.data.length > 0 ? (
          <SearchInput
            id="member-search"
            label="Search approved contacts"
            placeholder="Name, email, role or team"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="mb-3 w-full max-w-sm"
          />
        ) : null}

        {members.data && members.data.length > 0 && memberRows.length === 0 ? (
          <Panel>
            <EmptyState title="No contacts match" message="Try a different search." />
          </Panel>
        ) : null}

        {change.error ? <ErrorNotice inline title="Could not save the change" message={errorMessage(change.error)} /> : null}
        {resend.error ? <ErrorNotice inline title="Could not resend the invitation" message={errorMessage(resend.error)} /> : null}
        {resentTo ? (
          <p role="status" className="mb-3 rounded-md bg-emerald-50 p-3 text-sm text-emerald-800">
            A new invitation is on its way to {resentTo}. The earlier link no longer works. If it doesn't arrive, ask them to check their junk folder.
          </p>
        ) : null}

        {memberRows.length > 0 ? (
          <>
            {/* Phones: one card per person, the role and remove controls under the name. */}
            <ul className="grid gap-3 md:hidden">
              {memberRows.map((member) => (
                <li key={member.id}>
                  <Panel className={`p-4 ${member.isActive ? '' : 'opacity-70'}`}>
                    <MemberName member={member} />
                    <MemberControls member={member} busy={change.isPending || resend.isPending} onRole={(role) => change.mutate({ id: member.id, role })} onRemove={() => setRemoving(member)} onRestore={() => change.mutate({ id: member.id, isActive: true })} onResend={() => resend.mutate(member)} />
                  </Panel>
                </li>
              ))}
            </ul>
            <div className="hidden md:block">
              <DataTable headers={['Contact', 'Role', 'Teams', 'Authority', '']}>
                {memberRows.map((member) => (
                  <tr
                    key={member.id}
                    className={`border-t border-portal-line align-middle text-portal-tiny ${member.isActive ? '' : 'opacity-70'}`}
                  >
                    <td className="px-4 py-2.5">
                      <MemberName member={member} />
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <RoleSelect member={member} busy={change.isPending} onRole={(role) => change.mutate({ id: member.id, role })} />
                    </td>
                    <td className="px-4 py-2.5 text-portal-muted">
                      {member.teams.length === 0
                        ? '—'
                        : member.teams
                            .map((team) =>
                              team.role === 'TEAM_ADMIN' ? `${team.name} (admin)` : team.name,
                            )
                            .join(' · ')}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap text-portal-muted">
                      {member.creditApprovalLimit != null
                        ? `Up to ${formatCredits(member.creditApprovalLimit)} credits`
                        : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <span className="inline-flex items-center gap-2">
                        <ResendButton member={member} busy={resend.isPending} onResend={() => resend.mutate(member)} />
                        <RemoveButton member={member} busy={change.isPending} onRemove={() => setRemoving(member)} onRestore={() => change.mutate({ id: member.id, isActive: true })} />
                      </span>
                    </td>
                  </tr>
                ))}
              </DataTable>
            </div>
          </>
        ) : null}

        <ConfirmDialog
          open={removing !== null}
          tone="danger"
          title={removing?.isPending ? `Cancel ${displayName(removing)}'s invitation?` : `Remove ${removing ? displayName(removing) : 'this person'}?`}
          message={removing?.isPending ? "The link in their email stops working. Restoring them later sends a new invitation." : 'They lose access to the portal right away. You can restore them later.'}
          confirmLabel="Remove"
          onConfirm={() => {
            if (removing) change.mutate({ id: removing.id, isActive: false });
          }}
          onCancel={() => {
            if (!change.isPending) setRemoving(null);
          }}
          pending={change.isPending}
        />
      </div>
    </div>
  );
}

function displayName(member: Member): string {
  return member.firstName || member.lastName ? `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim() : member.email;
}

function MemberName({ member }: { readonly member: Member }): React.ReactElement {
  return (
    <>
      <span className="text-portal-small font-bold">{displayName(member)}</span>
      {member.isPending ? (
        <Pill tone="warning" className="ml-2">
          Invited
        </Pill>
      ) : null}
      {member.isActive ? null : (
        <Pill tone="neutral" className="ml-2">
          Removed
        </Pill>
      )}
      <span className="block break-all text-portal-micro text-portal-muted">{member.email}</span>
    </>
  );
}

interface MemberActionProps {
  readonly member: Member;
  readonly busy: boolean;
}

function RoleSelect({ member, busy, onRole }: MemberActionProps & { readonly onRole: (role: OrgRole) => void }): React.ReactElement {
  return (
    <SelectInput
      id={`member-role-${member.id}`}
      aria-label={`Role for ${displayName(member)}`}
      value={member.role}
      disabled={busy || !member.isActive}
      onChange={(event) => onRole(event.target.value as OrgRole)}
    >
      {(Object.keys(ORG_ROLE_LABELS) as OrgRole[]).map((value) => (
        <option key={value} value={value}>
          {ORG_ROLE_LABELS[value]}
        </option>
      ))}
    </SelectInput>
  );
}

function RemoveButton({ member, busy, onRemove, onRestore }: MemberActionProps & { readonly onRemove: () => void; readonly onRestore: () => void }): React.ReactElement {
  return member.isActive ? (
    <Button variant="secondary" disabled={busy} onClick={onRemove}>
      Remove
    </Button>
  ) : (
    <Button variant="secondary" disabled={busy} onClick={onRestore}>
      Restore
    </Button>
  );
}

/** Sends a fresh invitation to someone who has not accepted yet. */
function ResendButton({ member, busy, onResend }: MemberActionProps & { readonly onResend: () => void }): React.ReactElement | null {
  if (!member.isPending || !member.isActive) return null;
  return (
    <Button variant="secondary" disabled={busy} onClick={onResend}>
      Resend invite
    </Button>
  );
}

function MemberControls(props: MemberActionProps & { readonly onRole: (role: OrgRole) => void; readonly onRemove: () => void; readonly onRestore: () => void; readonly onResend: () => void }): React.ReactElement {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-3">
      <RoleSelect {...props} />
      <ResendButton {...props} />
      <RemoveButton {...props} />
    </div>
  );
}

interface FieldProps {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
}

/**
 * A labelled single-line text field.
 *
 * @param props - Field identity and value.
 * @returns The field.
 */
function TextField({ id, label, value, onChange, placeholder }: FieldProps): React.ReactElement {
  return (
    <Field label={label} htmlFor={id}>
      <TextInput
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder ?? ''}
      />
    </Field>
  );
}

/**
 * A labelled multi-line text field.
 *
 * @param props - Field identity and value.
 * @returns The field.
 */
function TextAreaField({ id, label, value, onChange }: FieldProps): React.ReactElement {
  return (
    <Field label={label} htmlFor={id}>
      <TextArea
        id={id}
        rows={2}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </Field>
  );
}

interface ColorFieldProps {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
}

/**
 * A color swatch paired with its hex value.
 *
 * @param props - Field identity and value.
 * @returns The color field.
 */
function ColorField({ id, label, value, onChange }: ColorFieldProps): React.ReactElement {
  return (
    <div>
      <label htmlFor={id} className="block text-portal-tiny font-bold">
        {label}
      </label>
      <div className="mt-2 flex items-center gap-2">
        <input
          id={id}
          type="color"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="size-10 cursor-pointer rounded-md border border-admin-line bg-white p-1"
        />
        <input
          aria-label={`${label} hex value`}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="h-10 w-28 rounded-md border border-admin-line bg-white px-2 font-mono text-sm"
        />
      </div>
    </div>
  );
}

/** One curated program, as the catalog tab lists it. */
export interface CatalogueRow {
  readonly id: string;
  readonly v3ProgramId: string;
  readonly name: string;
  readonly imageUrl: string | null;
  readonly isEnabled: boolean;
  readonly isFeatured: boolean;
  /** What the client is actually charged, template resolved. */
  readonly startingCredits: number;
  /** Effective lead time — the template's when one is attached. */
  readonly leadTimeDays: number;
  readonly requiresReview: boolean;
  readonly pricingTemplateId: string | null;
  readonly pricingTemplateName: string | null;
  /** The entry's own ad-hoc values, for the pricing editor. */
  readonly tiers: readonly { readonly upTo: number; readonly credits: number }[];
  readonly baseCredits: number;
  readonly includedParticipants: number;
  readonly incrementSize: number;
  readonly incrementCredits: number;
  readonly ownLeadTimeDays: number;
}

/** A program that could be added to the catalog. */
export interface CatalogueableProgram {
  readonly v3ProgramId: string;
  readonly name: string;
  readonly category: string | null;
}

interface CataloguePanelProps {
  readonly organizationId: string;
  readonly organizationName: string;
}

/**
 * Curates which BCE programs a client can book, and what they cost in credits.
 *
 * A client's catalog is empty until someone adds to it — this is where that
 * happens. Search the program library, add a program (seeded with the default
 * credit rule), enable or disable it, and remove it when it no longer applies.
 *
 * @param props - The organization to curate.
 * @returns The catalog tab.
 */
function CataloguePanel({
  organizationId,
  organizationName,
}: CataloguePanelProps): React.ReactElement {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const debounced = useDebouncedValue(search, 250);
  const [removing, setRemoving] = useState<CatalogueRow | null>(null);
  const [pricing, setPricing] = useState<CatalogueRow | null>(null);

  const entries = useQuery({
    queryKey: ['catalog', organizationId],
    queryFn: ({ signal }) =>
      getJson<CatalogueRow[]>(`/organizations/${organizationId}/catalogue`, undefined, signal),
  });

  const options = useQuery({
    queryKey: ['catalogueable', organizationId, debounced],
    queryFn: ({ signal }) =>
      getJson<CatalogueableProgram[]>(
        `/organizations/${organizationId}/catalogue/programs`,
        debounced.trim() === '' ? undefined : { search: debounced.trim() },
        signal,
      ),
    enabled: debounced.trim().length > 0,
  });

  /** Invalidates both the roster and the picker after any change. */
  async function refresh(): Promise<void> {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['catalog', organizationId] }),
      queryClient.invalidateQueries({ queryKey: ['catalogueable', organizationId] }),
    ]);
  }

  const add = useMutation({
    mutationFn: (v3ProgramId: string) =>
      sendJson('post', `/organizations/${organizationId}/catalogue`, { v3ProgramId }),
    onSuccess: refresh,
  });

  const toggle = useMutation({
    mutationFn: (input: { id: string; isEnabled: boolean }) =>
      sendJson('patch', `/organizations/${organizationId}/catalogue/${input.id}`, {
        isEnabled: input.isEnabled,
      }),
    // Optimistic: the pill flips the moment it's clicked. Hide/Show is
    // trivially reversible and almost never fails, so waiting a round trip
    // just makes the button feel broken.
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: ['catalog', organizationId] });
      const previous = queryClient.getQueryData<CatalogueRow[]>(['catalog', organizationId]);
      queryClient.setQueryData<CatalogueRow[]>(['catalog', organizationId], (rows) =>
        rows?.map((row) => (row.id === input.id ? { ...row, isEnabled: input.isEnabled } : row)),
      );
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['catalog', organizationId], context.previous);
      }
    },
    onSettled: refresh,
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      sendJson('delete', `/organizations/${organizationId}/catalogue/${id}`),
    onSuccess: async () => {
      setRemoving(null);
      await refresh();
    },
  });

  return (
    <div className="mt-6">
      <TabHeading
        title="Catalog"
        subtitle={`The programs ${organizationName} can book, and their starting credit cost. New clients start empty — add programs here.`}
      />

      <Panel guide="org-catalog-add" className="p-5">
        <label htmlFor="catalog-search" className="block text-portal-tiny font-bold">
          Add a program
        </label>
        <div className="mt-1">
          <SearchInput
            id="catalog-search"
            label="Search the program library"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search the program library…"
          />
        </div>

        {debounced.trim().length === 0 ? null : options.isPending ? (
          <p className="mt-2 text-portal-small text-portal-muted">Searching…</p>
        ) : options.isError ? (
          <ErrorNotice inline message={errorMessage(options.error)} />
        ) : options.data.length === 0 ? (
          <p className="mt-2 text-portal-small text-portal-muted">
            No programs match — or every match is already in the catalog.
          </p>
        ) : (
          <ul className="mt-2 divide-y divide-portal-line rounded-portal-card border border-portal-line">
            {options.data.slice(0, 8).map((program) => (
              <li
                key={program.v3ProgramId}
                className="flex items-center justify-between gap-3 px-3 py-2"
              >
                <span className="min-w-0">
                  <span className="block text-portal-small font-bold">{program.name}</span>
                  {program.category === null ? null : (
                    <span className="block text-portal-micro text-portal-muted">
                      {program.category}
                    </span>
                  )}
                </span>
                <Button
                  variant="secondary"
                  disabled={add.isPending}
                  onClick={() => add.mutate(program.v3ProgramId)}
                >
                  Add
                </Button>
              </li>
            ))}
          </ul>
        )}
        {add.isError ? <ErrorNotice inline message={errorMessage(add.error)} /> : null}
      </Panel>

      <div className="mt-8">
        <TabHeading title="In the catalog" />
        {entries.isError ? (
          <ErrorNotice
            title="Couldn’t load the catalog"
            message={errorMessage(entries.error)}
            action={
              <Button variant="secondary" onClick={() => void entries.refetch()}>
                Try again
              </Button>
            }
          />
        ) : entries.isPending ? (
          <SkeletonTable rows={4} columns={['w-[45%]', 'w-[15%]', 'w-[15%]']} label="Loading catalog…" />
        ) : entries.data.length === 0 ? (
          <Panel>
            <EmptyState
              icon={<LayoutGridIcon aria-hidden className="size-6" />}
              title="No programs yet"
              message="Search above to add the first program this client can book."
            />
          </Panel>
        ) : (
          <DataTable
            headers={['Program', 'Credits', 'Lead time', 'Status', 'Actions']}
            visuallyHiddenHeaders={[4]}
          >
            {entries.data.map((entry) => (
              <tr
                key={entry.id}
                className="border-t border-portal-line align-middle text-portal-tiny"
              >
                <td className="px-4 py-2.5">
                  <Link
                    to={`/programs/${entry.v3ProgramId}`}
                    className="group flex items-center gap-3"
                  >
                    {entry.imageUrl ? (
                      <img
                        src={entry.imageUrl}
                        alt=""
                        className="h-[44px] w-[66px] shrink-0 rounded-[6px] border border-portal-line object-cover"
                      />
                    ) : (
                      <span
                        aria-hidden="true"
                        className="h-[44px] w-[66px] shrink-0 rounded-[6px] bg-gradient-to-br from-portal-sky to-[#dce5f2]"
                      />
                    )}
                    <span className="min-w-0">
                      <span className="block text-portal-small font-bold underline-offset-2 group-hover:underline">
                        {entry.name}
                      </span>
                      {entry.isFeatured ? (
                        <span className="block text-portal-micro text-portal-muted">Featured</span>
                      ) : null}
                    </span>
                  </Link>
                </td>
                <td className="px-4 py-2.5 whitespace-nowrap">
                  <span className="block">
                    {entry.requiresReview ? 'Individually priced' : `From ${formatCredits(entry.startingCredits)}`}
                  </span>
                  <span className="block text-portal-micro text-portal-muted">
                    {entry.pricingTemplateName ?? 'Ad hoc'}
                  </span>
                </td>
                <td className="px-4 py-2.5 whitespace-nowrap text-portal-muted">
                  {entry.leadTimeDays} days
                </td>
                <td className="px-4 py-2.5">
                  {entry.isEnabled ? (
                    <Pill tone="success">Live</Pill>
                  ) : (
                    <Pill tone="neutral">Hidden</Pill>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => setPricing(entry)}
                    className="cursor-pointer text-portal-micro font-semibold text-brand hover:underline"
                  >
                    Pricing
                  </button>
                  <button
                    type="button"
                    onClick={() => toggle.mutate({ id: entry.id, isEnabled: !entry.isEnabled })}
                    className="ml-3 cursor-pointer text-portal-micro font-semibold text-brand hover:underline"
                  >
                    {entry.isEnabled ? 'Hide' : 'Show'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setRemoving(entry)}
                    className="ml-3 cursor-pointer text-portal-micro font-semibold text-rose-600 hover:underline"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </DataTable>
        )}
        {toggle.isError ? (
          <ErrorNotice inline title="Could not update" message={errorMessage(toggle.error)} />
        ) : null}
      </div>

      <ConfirmDialog
        open={removing !== null}
        tone="danger"
        title={`Remove ${removing ? removing.name : 'this program'} from the catalog?`}
        message={`${organizationName} will no longer be able to book it. The program itself is unaffected, and you can add it back later.`}
        confirmLabel="Remove program"
        onConfirm={() => {
          if (removing) remove.mutate(removing.id);
        }}
        onCancel={() => {
          if (!remove.isPending) setRemoving(null);
        }}
        pending={remove.isPending}
        error={remove.isError ? errorMessage(remove.error) : undefined}
      />

      <Drawer
        open={pricing !== null}
        title="Pricing"
        subtitle={pricing?.name}
        onClose={() => setPricing(null)}
      >
        {pricing === null ? null : (
          <PricingForm
            key={pricing.id}
            entry={pricing}
            onSave={async (patch) => {
              await sendJson('patch', `/organizations/${organizationId}/catalogue/${pricing.id}`, patch);
              setPricing(null);
              await refresh();
            }}
          />
        )}
      </Drawer>
    </div>
  );
}

/** What the pricing form PATCHes onto a catalog entry. */
interface PricingPatch {
  readonly pricingTemplateId: string | null;
  readonly tiers?: readonly { upTo: number; credits: number }[];
  readonly incrementSize?: number;
  readonly incrementCredits?: number;
  readonly leadTimeDays?: number;
  readonly requiresReview: boolean;
}

/**
 * One program's pricing: follow a template, or price it ad hoc.
 *
 * A template keeps the program on BCE's rate card — its breakpoints, increment
 * rule and lead time all come from the template and move when it is edited.
 * Ad hoc opens the same structure for this program alone.
 *
 * @param props - The catalog entry and the save action.
 * @returns The form.
 */
function PricingForm({
  entry,
  onSave,
}: {
  readonly entry: CatalogueRow;
  readonly onSave: (patch: PricingPatch) => Promise<void>;
}): React.ReactElement {
  const templates = useQuery({
    queryKey: ['pricing-templates'],
    queryFn: ({ signal }) => getJson<PricingTemplate[]>('/pricing-templates', undefined, signal),
  });

  const [templateId, setTemplateId] = useState(entry.pricingTemplateId ?? '');
  const [tiers, setTiers] = useState<readonly TierDraft[]>(
    entry.tiers.length > 0
      ? entry.tiers.map((tier) => ({ upTo: String(tier.upTo), credits: String(tier.credits) }))
      : [{ upTo: String(entry.includedParticipants), credits: String(entry.baseCredits) }],
  );
  const [incrementSize, setIncrementSize] = useState(String(entry.incrementSize));
  const [incrementCredits, setIncrementCredits] = useState(String(entry.incrementCredits));
  const [leadTimeDays, setLeadTimeDays] = useState(String(entry.ownLeadTimeDays));
  const [requiresReview, setRequiresReview] = useState(entry.requiresReview);

  const chosen = templates.data?.find((template) => template.id === templateId) ?? null;
  const parsed = parseTiers(tiers);
  const adHocValid =
    parsed.length > 0 &&
    parsed.length === tiers.length &&
    tiersAscend(parsed) &&
    Number(incrementSize) > 0 &&
    Number(incrementCredits) >= 0 &&
    Number(leadTimeDays) >= 0;
  const valid = templateId !== '' ? true : adHocValid;

  const save = useMutation({
    mutationFn: () =>
      onSave(
        templateId !== ''
          ? { pricingTemplateId: templateId, requiresReview }
          : {
              pricingTemplateId: null,
              tiers: parsed,
              incrementSize: Number(incrementSize),
              incrementCredits: Number(incrementCredits),
              leadTimeDays: Number(leadTimeDays),
              requiresReview,
            },
      ),
  });

  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid && !save.isPending) save.mutate();
      }}
    >
      <Field
        label="Pricing structure"
        htmlFor="pricing-template"
        hint="Templates are managed under Pricing templates — editing one reprices every program using it."
      >
        <SelectInput
          id="pricing-template"
          value={templateId}
          disabled={templates.isPending}
          onChange={(event) => setTemplateId(event.target.value)}
        >
          <option value="">Ad hoc — this program only</option>
          {(templates.data ?? []).map((template) => (
            <option key={template.id} value={template.id}>
              {template.name}
            </option>
          ))}
        </SelectInput>
      </Field>

      {chosen ? (
        <div className="rounded-portal-button bg-portal-bg p-3.5 text-portal-tiny">
          <b className="block">{chosen.name}</b>
          <span className="mt-1 block tabular-nums">{tiersSummary(chosen)}</span>
          <span className="block text-portal-muted">
            then +{formatCredits(chosen.incrementCredits)} per {chosen.incrementSize} people ·{' '}
            {chosen.leadTimeDays}-day lead
          </span>
        </div>
      ) : (
        <>
          <TierEditor tiers={tiers} onChange={setTiers} disabled={save.isPending} />

          <div>
            <span className="block text-portal-tiny font-bold">Beyond the last breakpoint</span>
            <div className="mt-1.5 flex items-center gap-2">
              <span className="text-portal-tiny text-portal-muted">Add</span>
              <TextInput
                aria-label="Credits per additional increment"
                type="number"
                min={0}
                value={incrementCredits}
                disabled={save.isPending}
                onChange={(event) => setIncrementCredits(event.target.value)}
                className="mt-0 w-[80px]"
              />
              <span className="text-portal-tiny text-portal-muted">credits per additional</span>
              <TextInput
                aria-label="People per increment"
                type="number"
                min={1}
                value={incrementSize}
                disabled={save.isPending}
                onChange={(event) => setIncrementSize(event.target.value)}
                className="mt-0 w-[80px]"
              />
              <span className="text-portal-tiny text-portal-muted">people</span>
            </div>
          </div>

          <Field label="Lead time (days)" htmlFor="pricing-lead">
            <TextInput
              id="pricing-lead"
              type="number"
              min={0}
              max={365}
              value={leadTimeDays}
              onChange={(event) => setLeadTimeDays(event.target.value)}
              className="w-[120px]"
            />
          </Field>
        </>
      )}

      <label className="flex items-center gap-2 text-portal-tiny">
        <input
          type="checkbox"
          checked={requiresReview}
          onChange={(event) => setRequiresReview(event.target.checked)}
        />
        Individually priced — show &ldquo;BCE review&rdquo; instead of a firm figure
      </label>

      {parsed.length > 0 && templateId === '' && !tiersAscend(parsed) ? (
        <ErrorNotice
          inline
          message="Breakpoints must increase — each “up to” must be larger than the one before it."
        />
      ) : null}
      {save.isError ? (
        <ErrorNotice inline title="Could not save" message={errorMessage(save.error)} />
      ) : null}

      <Button type="submit" disabled={!valid || save.isPending}>
        {save.isPending ? 'Saving…' : 'Save pricing'}
      </Button>
    </form>
  );
}

/**
 * A short date for table rows.
 *
 * @param iso - ISO timestamp.
 * @returns e.g. "Aug 8, 2026".
 */
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** The enquiry pipeline stages, in order, with staff-facing labels. */
const LARGE_EVENT_STAGES = [
  ['INITIAL_INQUIRY', 'Inquiry received'],
  ['DISCOVERY_SCHEDULED', 'Discovery call scheduled'],
  ['OBJECTIVES_CONFIRMED', 'Objectives confirmed'],
  ['OPTIONS_PRESENTED', 'Options presented'],
  ['FEASIBILITY_REVIEW', 'Feasibility review'],
  ['BUDGET_REVIEW', 'Budget review'],
  ['PROPOSAL_IN_DEVELOPMENT', 'Proposal in development'],
  ['PROPOSAL_SUBMITTED', 'Proposal submitted'],
  ['PROCUREMENT_REVIEW', 'Procurement review'],
  ['AGREEMENT_SIGNED', 'Agreement signed'],
  ['PLANNING_UNDERWAY', 'Planning underway'],
  ['STAFFING_CONFIRMED', 'Staffing confirmed'],
  ['DELIVERED', 'Delivered'],
  ['RECAP_COMPLETED', 'Recap completed'],
  ['CLOSED_LOST', 'Closed'],
] as const;

/** One large-event enquiry, as the admin lists it. */
export interface EnquiryRow {
  readonly id: string;
  readonly reference: string;
  readonly status: string;
  readonly eventName: string;
  readonly eventType: string | null;
  readonly estimatedParticipants: number | null;
  readonly budgetRange: string | null;
  readonly decisionTimeline: string | null;
  readonly purpose: string | null;
  readonly locations: readonly string[];
  readonly createdAt: string;
}

/**
 * The client's large-event inquiries — the pipeline for events beyond the
 * credit package. Staff read what was submitted and advance each inquiry's
 * stage; before this, enquiries were written and never seen.
 *
 * @param props - The organization whose enquiries to manage.
 * @returns The enquiries tab.
 */
function EnquiriesPanel({ organizationId }: { organizationId: string }): React.ReactElement {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  const inquiries = useQuery({
    queryKey: ['org-enquiries', organizationId],
    queryFn: ({ signal }) =>
      getJson<EnquiryRow[]>(`/organizations/${organizationId}/large-events`, undefined, signal),
  });

  const advance = useMutation({
    mutationFn: (input: { id: string; status: string }) =>
      sendJson('patch', `/organizations/${organizationId}/large-events/${input.id}`, {
        status: input.status,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['org-enquiries', organizationId] }),
  });

  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <TabHeading
          title="Large and custom event inquiries"
          subtitle="Events beyond the credit package. Move each inquiry along as the conversation develops."
        />
        <SearchInput
          id="large-event-search"
          label="Search large and custom event inquiries"
          className="w-72"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search inquiries…"
        />
      </div>

      <div>
        {inquiries.isError ? (
          <ErrorNotice
            title="Couldn’t load inquiries"
            message={errorMessage(inquiries.error)}
            action={
              <Button variant="secondary" onClick={() => void inquiries.refetch()}>
                Try again
              </Button>
            }
          />
        ) : inquiries.isPending ? (
          <SkeletonTable rows={3} columns={['w-[45%]', 'w-[20%]']} label="Loading inquiries…" />
        ) : inquiries.data.length === 0 ? (
          <Panel>
            <EmptyState
              icon={<MessagesSquareIcon aria-hidden className="size-6" />}
              title="No inquiries yet"
              message="When this client asks about a large or custom event, it appears here."
            />
          </Panel>
        ) : (
          <DataTable headers={['Inquiry', 'Type', 'Participants', 'Budget', 'Timeline', 'Stage']}>
            {matching(inquiries.data, search, (row) => [row.reference, row.eventName, row.eventType, row.purpose]).map((inquiry) => (
              <tr
                key={inquiry.id}
                className="border-t border-portal-line align-middle text-portal-tiny"
              >
                <td className="max-w-[320px] px-4 py-2.5">
                  <Link
                    to={`/organizations/${organizationId}/enquiries/${inquiry.id}`}
                    className="block truncate text-portal-small font-bold underline-offset-2 hover:underline"
                    title={inquiry.purpose ?? undefined}
                  >
                    {inquiry.eventName}
                  </Link>
                  <span className="block text-portal-micro text-portal-muted">
                    {[inquiry.reference, formatDate(inquiry.createdAt)].join(' · ')}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-portal-muted">{inquiry.eventType ?? '—'}</td>
                <td className="px-4 py-2.5 whitespace-nowrap tabular-nums">
                  {inquiry.estimatedParticipants ?? '—'}
                </td>
                <td className="px-4 py-2.5 whitespace-nowrap text-portal-muted">
                  {inquiry.budgetRange ?? '—'}
                </td>
                <td className="px-4 py-2.5 whitespace-nowrap text-portal-muted">
                  {inquiry.decisionTimeline ?? '—'}
                </td>
                <td className="px-4 py-2.5">
                  <label htmlFor={`inquiry-status-${inquiry.id}`} className="sr-only">
                    Stage for {inquiry.eventName}
                  </label>
                  <SelectInput
                    density="compact"
                    id={`inquiry-status-${inquiry.id}`}
                    value={inquiry.status}
                    disabled={advance.isPending}
                    onChange={(event) => advance.mutate({ id: inquiry.id, status: event.target.value })}
                  >
                    {LARGE_EVENT_STAGES.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </SelectInput>
                </td>
              </tr>
            ))}
          </DataTable>
        )}
        {advance.isError ? <ErrorNotice inline message={errorMessage(advance.error)} /> : null}
      </div>
    </div>
  );
}

/** A client's request to move an expiry, as the console shows it (§21). */
export interface CreditChangeRow {
  readonly id: string;
  readonly kind: 'EXTEND' | 'FORWARD';
  readonly status: 'REQUESTED' | 'APPROVED' | 'DECLINED';
  readonly credits: number;
  readonly currentExpiry: string;
  readonly requestedExpiry: string;
  readonly reason: string | null;
  readonly decidedByName: string | null;
  readonly decisionNote: string | null;
}

/**
 * The client's forward and extend requests, and BCE's decision on them (§21).
 *
 * Approving writes a pair of ledger rows that net to zero — the balance does
 * not move, only the date attached to it — so this queue is the only place the
 * date of a tranche can change after it is bought.
 *
 * @param props - The organization whose requests to show.
 * @returns The queue, or nothing when the client has never asked.
 */
function CreditChangeQueue({
  organizationId,
}: {
  readonly organizationId: string;
}): React.ReactElement | null {
  const queryClient = useQueryClient();

  const requests = useQuery({
    queryKey: ['org-credit-changes', organizationId],
    queryFn: ({ signal }) =>
      getJson<CreditChangeRow[]>(
        `/organizations/${organizationId}/credit-change-requests`,
        undefined,
        signal,
      ),
  });

  const decide = useMutation({
    mutationFn: ({ id, approve }: { id: string; approve: boolean }) =>
      sendJson(
        'post',
        `/organizations/${organizationId}/credit-change-requests/${id}/decision`,
        { approve },
      ),
    onSuccess: async () => {
      // Approving moves the ledger, so the balances above are stale too.
      await queryClient.invalidateQueries({ queryKey: ['org-credit-changes', organizationId] });
      await queryClient.invalidateQueries({ queryKey: ['org-credits', organizationId] });
      await queryClient.invalidateQueries({ queryKey: ['org-ledger', organizationId] });
    },
  });

  const rows = requests.data ?? [];
  if (rows.length === 0) return null;

  const pending = rows.filter((row) => row.status === 'REQUESTED');

  return (
    <Panel className="mb-4 p-6">
      <h3 className="text-lg font-semibold">
        Expiry requests
        {pending.length > 0 ? (
          <span className="ml-2 text-sm font-normal text-muted-foreground">
            {pending.length} awaiting a decision
          </span>
        ) : null}
      </h3>

      <ul className="mt-3 divide-y">
        {rows.map((row) => (
          <li key={row.id} className="flex flex-wrap items-center gap-3 py-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm">
                <b>{formatCredits(row.credits)} credits</b>{' '}
                {row.kind === 'FORWARD' ? 'carried forward' : 'extended'} from{' '}
                {row.currentExpiry.slice(0, 10)} to {row.requestedExpiry.slice(0, 10)}
              </p>
              {row.reason ? (
                <p className="text-xs text-muted-foreground">{row.reason}</p>
              ) : null}
              {row.status !== 'REQUESTED' ? (
                <p className="text-xs text-muted-foreground">
                  {row.status === 'APPROVED' ? 'Approved' : 'Declined'}
                  {row.decidedByName ? ` by ${row.decidedByName}` : ''}
                  {row.decisionNote ? ` — ${row.decisionNote}` : ''}
                </p>
              ) : null}
            </div>

            {row.status === 'REQUESTED' ? (
              <div className="flex gap-2">
                <Button
                  size="compact"
                  disabled={decide.isPending}
                  onClick={() => decide.mutate({ id: row.id, approve: true })}
                >
                  Approve
                </Button>
                <Button
                  size="compact"
                  variant="secondary"
                  disabled={decide.isPending}
                  onClick={() => decide.mutate({ id: row.id, approve: false })}
                >
                  Decline
                </Button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/** One booking in BCE's review queue (§22). */
/** The rows whose named fields contain the search, case-insensitively; all of them for a blank search. */
function matching<T>(rows: readonly T[], search: string, fields: (row: T) => readonly (string | null | undefined)[]): T[] {
  const needle = search.trim().toLowerCase();
  if (needle.length === 0) return [...rows];
  return rows.filter((row) => fields(row).some((field) => (field ?? '').toLowerCase().includes(needle)));
}

/** How a request's status reads to staff while it is still an inquiry. */
const REVIEW_STATUS_LABELS: Readonly<Record<string, string>> = {
  SUBMITTED: 'Submitted',
  AWAITING_TEAM_APPROVAL: 'Awaiting team approval',
  AWAITING_ORG_APPROVAL: 'Awaiting client approval',
  UNDER_BCE_REVIEW: 'Under review',
  DATE_HELD: 'Date held',
  INFORMATION_REQUIRED: 'Information needed',
};
const REVIEW_STATUS_TONES: Readonly<Record<string, PillTone>> = {
  UNDER_BCE_REVIEW: 'warning',
  DATE_HELD: 'info',
  INFORMATION_REQUIRED: 'danger',
};

export interface ReviewQueue {
  /** §22's factor list, sent by the server so the console keeps no copy. */
  readonly factors: readonly { key: string; label: string }[];
  readonly outcomes: readonly { value: string; label: string }[];
  readonly bookings: readonly ReviewQueueRow[];
}

export interface ReviewQueueRow {
  readonly id: string;
  readonly reference: string;
  readonly status: string;
  readonly programName: string;
  readonly preferredDate: string | null;
  readonly city: string | null;
  readonly estimatedAttendees: number;
  readonly estimatedCredits: number;
  readonly feasibility: {
    readonly factors: Record<string, string>;
    readonly factorNotes: Record<string, string>;
    readonly outcome: string | null;
    readonly suggestedOutcome: string;
    readonly complete: boolean;
    readonly unresolved: readonly { key: string; label: string; status: string }[];
    readonly clientNote: string | null;
    readonly reviewedByName: string | null;
    readonly reviewedAt: string | null;
  };
}

/**
 * The operational feasibility review (§22).
 *
 * The BCE approval step existed already, but it was a bare approve/reject: an
 * operations person held twenty considerations in their head and the record
 * kept one bit. This is where those twenty get written down, and where the one
 * line the client reads gets written.
 *
 * @param props - The organization whose queue to show.
 * @returns The review panel.
 */
function BookingReviewPanel({ organizationId }: { readonly organizationId: string }): React.ReactElement {
  const [search, setSearch] = useState('');

  const queue = useQuery({
    queryKey: ['org-review-queue', organizationId],
    queryFn: ({ signal }) =>
      getJson<ReviewQueue>(
        `/organizations/${organizationId}/bookings/review-queue`,
        undefined,
        signal,
      ),
  });

  const reviewRows = matching(queue.data?.bookings ?? [], search, (row) => [row.reference, row.programName, row.city, REVIEW_STATUS_LABELS[row.status]]);

  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <TabHeading
          title="Event requests"
          subtitle="Requests that would use credits, waiting on a decision. Open one to work it; promoting it makes it an event."
        />
        <SearchInput
          id="review-queue-search"
          label="Search requests under review"
          className="w-72"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search requests…"
        />
      </div>

      {queue.isPending ? (
        <LoadingState label="Loading the queue…" />
      ) : queue.error ? (
        <ErrorNotice inline message={errorMessage(queue.error)} />
      ) : (queue.data?.bookings ?? []).length === 0 ? (
        <Panel className="p-6">
          <EmptyState
            title="Nothing waiting"
            message="Requests appear here once they reach BCE review, are on hold, or need more information."
          />
        </Panel>
      ) : reviewRows.length === 0 ? (
        <Panel className="p-6">
          <EmptyState title="No matches" message={`Nothing under review matches “${search.trim()}”.`} />
        </Panel>
      ) : (
        <DataTable headers={['Request', 'Date', 'Location', 'Participants', 'Credits', 'Status', 'Actions']} rightAlignedHeaders={[3, 4]} visuallyHiddenHeaders={[6]}>
          {reviewRows.map((row) => (
            <tr key={row.id} className="border-t border-portal-line align-middle">
              <td className="p-3 text-portal-small">
                <Link to={`/organizations/${organizationId}/requests/${row.id}`} className="font-bold text-primary underline-offset-2 hover:underline">
                  {row.programName}
                </Link>
                <span className="block text-portal-micro text-portal-muted">{row.reference}</span>
              </td>
              <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">{row.preferredDate ? formatDate(row.preferredDate) : '—'}</td>
              <td className="p-3 text-portal-small text-portal-muted">{row.city ?? '—'}</td>
              <td className="p-3 text-portal-small text-right">{row.estimatedAttendees}</td>
              <td className="p-3 text-portal-small text-right">{row.estimatedCredits}</td>
              <td className="p-3 text-portal-small whitespace-nowrap">
                <Pill tone={REVIEW_STATUS_TONES[row.status] ?? 'neutral'}>{REVIEW_STATUS_LABELS[row.status] ?? row.status}</Pill>
                {row.feasibility.outcome ? <span className="ml-2 text-portal-micro text-portal-muted">{row.feasibility.outcome.replaceAll('_', ' ').toLowerCase()}</span> : null}
              </td>
              <td className="p-3 text-right whitespace-nowrap">
                <Button size="compact" variant="secondary" to={`/organizations/${organizationId}/requests/${row.id}`}>
                  Open
                </Button>
              </td>
            </tr>
          ))}
        </DataTable>
      )}
    </div>
  );
}


/**
 * A partner's showcase at a glance, for staff: what it is, what it shows,
 * whether the partner hears about leads, and the two places to see it.
 * The partner manages programs and words from their own portal.
 */
function PartnerShowcasePanel({
  partner,
  portalUrl,
}: {
  readonly partner: PartnerSummary | null;
  readonly portalUrl: string;
}): React.ReactElement {
  if (!partner) {
    return (
      <div className="mt-6">
        <Panel>
          <EmptyState
            title="Showcase not set up"
            message="The showcase is created the first time the partner, or you acting as admin, opens their portal."
            action={
              <Button variant="primary" href={portalUrl} target="_blank" rel="noreferrer">
                Open their portal
              </Button>
            }
          />
        </Panel>
      </div>
    );
  }
  const sites = partner.allowedOrigins.length;
  return (
    <div className="mt-6">
      <TabHeading
        title="Showcase"
        subtitle="The partner's program showcase on bestcorporateevents.com. They choose programs, colors and words from their portal; every request comes to sales."
      />
      <Panel className="p-5">
        <FactList
          columns={4}
          facts={[
            { label: 'Partner type', value: PARTNER_TYPE_LABELS[partner.partnerType] },
            {
              label: 'Programs',
              value: partner.programCount === 0 ? 'None yet' : `${partner.programCount.toLocaleString('en-US')} (${partner.featuredCount.toLocaleString('en-US')} featured)`,
            },
            { label: 'Lead notices', value: partner.notifyPartner ? 'On' : 'Off' },
            { label: 'Where it can appear', value: sites === 0 ? 'Any site' : partner.allowedOrigins.join(', ') },
          ]}
        />
        <div className="mt-5 flex flex-wrap gap-3 border-t border-portal-line pt-5">
          <Button variant="primary" href={partner.showcaseUrl} target="_blank" rel="noreferrer">
            View showcase ↗
          </Button>
          <Button variant="secondary" href={portalUrl} target="_blank" rel="noreferrer">
            Manage it in their portal ↗
          </Button>
        </div>
      </Panel>
    </div>
  );
}
